import type { Session, User } from '@powerful/data';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { analyticsRuntime } from '../lib/analytics';
import { claimsFromSession, type ParsedAuthClaims } from '../lib/auth/claims';
import { getSupabaseClient } from '../lib/supabase';

/**
 * Sessão viva da aplicação.
 *
 * Existe como contexto, e não como leitura por componente, porque a sessão é estado **global**:
 * resolvê-la em cada sítio daria N leituras assíncronas dessincronizadas, e o ecrã mostraria
 * "autenticado" numa zona e "anónimo" noutra durante o mesmo render.
 *
 * Os claims vêm do `parseAuthClaims` da T1.8 — este módulo não reimplementa a leitura do papel.
 * E vale a distinção de sempre: isto decide **navegação**; a autorização de **dados** é da RLS.
 */

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  claims: ParsedAuthClaims;
  isLoading: boolean;
}

const claimsNeutros: ParsedAuthClaims = {
  role: null,
  status: null,
  businessId: null,
  isInteractive: false,
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Lê os claims da sessão.
 *
 * O hook da T1.8 escreve `user_role`/`user_status`/`business_id` **só no access token** — o GoTrue
 * não os copia para `session.user` (verificado no stack local na T1.17; até então este contexto lia
 * `user` e resolvia sempre para claims neutros). Decodificar aqui não valida o token: quem o valida
 * é o servidor, a cada pedido, e a autorização de dados continua na RLS.
 */
function lerClaims(session: Session | null): ParsedAuthClaims {
  if (!session?.access_token) return claimsNeutros;
  return claimsFromSession(session);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  // 🔴 Arranca em `true`, não em "anónimo": ver o teste do primeiro render. Um guard que decide
  // antes de a sessão resolver expulsa utilizadores autenticados a cada refresh da página.
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let ativo = true;

    // 🔴 `getSupabaseClient()` **lança** quando faltam as `VITE_SUPABASE_*`. Chamá-lo fora do
    // try/catch derrubava a árvore inteira — e com ela o `AppShell`, que é onde vive o skip link.
    // O CI apanhou isto: o E2E de teclado reprovou em `/`, `/login` e no 404, porque o job de
    // acessibilidade não define aquelas variáveis.
    //
    // Um app sem backend configurado é um app **sem sessão**, não um app partido: a navegação
    // pública e a acessibilidade do documento não dependem de haver Supabase.
    let auth: ReturnType<typeof getSupabaseClient>['auth'];
    try {
      auth = getSupabaseClient().auth;
    } catch {
      setSession(null);
      setIsLoading(false);
      return;
    }

    void (async () => {
      try {
        const { data } = await auth.getSession();
        if (ativo) setSession(data.session ?? null);
      } catch {
        // Falha de transporte resolve para anónimo. Deixar `isLoading` preso seria pior: a app
        // ficaria num spinner permanente sem nunca dizer porquê.
        if (ativo) setSession(null);
      } finally {
        if (ativo) setIsLoading(false);
      }
    })();

    // Mantém o contexto alinhado com login, logout e refresh automático do token.
    const { data } = auth.onAuthStateChange((_evento, novaSessao) => {
      if (!ativo) return;
      setSession(novaSessao ?? null);
      setIsLoading(false);
    });

    return () => {
      ativo = false;
      data.subscription.unsubscribe();
    };
  }, []);

  // T1.18 · ADR 0038: a identidade na analytics acompanha **a pessoa**, não o objeto sessão — o
  // refresh do token troca a sessão a cada ~15 min e não pode reidentificar nem limpar. Só o id;
  // o consentimento é decidido dentro do runtime, que guarda o pedido até ele existir.
  const userId = session?.user?.id ?? null;
  const identidadeAplicada = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    const anterior = identidadeAplicada.current;
    if (anterior === userId) return;

    // Logout (inclui a exclusão da T1.17, que termina em signOutEverywhere) ou troca de pessoa: o
    // próximo utilizador do dispositivo não herda a identidade do anterior.
    if (anterior) analyticsRuntime.resetIdentity();
    if (userId) analyticsRuntime.identify(userId);

    identidadeAplicada.current = userId;
  }, [userId, isLoading]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      claims: lerClaims(session),
      isLoading,
    }),
    [session, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Acesso ao contexto. Lança fora do provider, em vez de devolver `undefined` silencioso. */
export function useAuthContext(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuthContext tem de ser usado dentro de <AuthProvider>.');
  }
  return value;
}
